import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Crown from "../components/Crown";
import PasswordInput from "../components/PasswordInput";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to your estate.");
      const dest = location.state?.from || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-textmain flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden border-r border-white/5">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/21926654/pexels-photo-21926654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-obsidian/95 via-obsidian/70 to-obsidian/95" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <Crown className="w-10 h-10" strokeWidth={1.2} />
            <div>
              <span className="font-heading text-3xl tracking-wide block leading-none">Aurethion</span>
              <span className="wordmark-nexus text-sm">Crown</span>
            </div>
          </div>
        </div>
        <div className="relative max-w-lg">
          <p className="label-eyebrow mb-6">Enterprise Governance · Intelligence · Authority</p>
          <h1 className="font-heading text-5xl leading-[1.05] tracking-tight mb-6">
            Aurethion Crown governs.
          </h1>
          <p className="text-textmuted leading-relaxed">
            One authoritative command surface for every R.S.V.P. business — governance,
            delegation, approvals, KPIs, risks and the AI agents that support them all.
          </p>
        </div>
        <div className="relative gold-divider" />
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-md regal-card p-10"
        >
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Crown className="w-8 h-8" strokeWidth={1.2} />
            <div>
              <span className="font-heading text-2xl block leading-none">Aurethion</span>
              <span className="wordmark-nexus text-[11px]">Crown</span>
            </div>
          </div>
          <p className="label-eyebrow">Sign In</p>
          <h2 className="font-heading text-3xl mt-2 mb-8">Welcome back</h2>

          <label className="block label-eyebrow mb-2" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full mb-5 bg-obsidian border border-seam rounded-md px-4 py-3 text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
            placeholder="you@domain.com"
          />

          <label className="block label-eyebrow mb-2" htmlFor="password">Password</label>
          <div className="mb-3">
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              testid="login-password-input"
              autoComplete="current-password"
            />
          </div>
          <div className="text-right mb-6">
            <Link
              to="/forgot"
              data-testid="link-forgot-password"
              className="text-xs text-textmuted hover:text-champagne transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all shadow-[0_0_20px_rgba(212,175,55,0.15)] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />}
            Enter the estate
          </button>

          <p className="text-sm text-textmuted text-center mt-6">
            New here?{" "}
            <Link
              to="/register"
              className="text-champagne hover:text-champagne-soft transition-colors"
              data-testid="link-register"
            >
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Login;
